import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";

const DURATION = 220;
const EASING = "cubic-bezier(0.2, 0, 0, 1)";
const FADE: KeyframeAnimationOptions = {
  duration: 160,
  delay: 60,
  easing: EASING,
  fill: "backwards"
};

const reducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type Box = { left: number; width: number };
type Rec = {
  el: HTMLElement;
  mode: string;
  from: Box;
  to: Box;
  bg: string;
  opacity: string;
  mark: number;
};

const keyOf = (el: HTMLElement): string => el.dataset.flipKey ?? "";
const modeOf = (el: HTMLElement): string => el.dataset.groupMode ?? "tile";
const lerp = (a: number, b: number, p: number): number => a + (b - a) * p;
const markOf = (el: HTMLElement): HTMLElement | null =>
  el.querySelector<HTMLElement>("[data-flip-mark]");

const clearFlags = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>("[data-flip-width]").forEach((el) => {
    delete el.dataset.flipWidth;
    delete el.dataset.resizing;
  });
};

/**
 * Animates `[data-flip-key]` children of `ref` from how they were drawn last
 * render to how they are now. Widths animate, so a tile opening into a group
 * (or a group closing into its tile) grows or shrinks in place and the tiles
 * after it ride along with the reflow; a translate covers what reflow can't,
 * like a closed tab's gap. The avatar (`[data-flip-mark]`) eases between its
 * tile and group sizes and the group's other contents fade in.
 *
 * While an element animates it carries `data-resizing` (so its contents can
 * clip instead of truncating) and `data-flip-width`, its final width, for
 * anything that measures the bar mid-animation. A render that changes the
 * layout mid-flight restarts from where things are drawn, not from layout.
 * The strip must be the elements' offset parent (`position: relative`).
 */
export function useFlip(ref: RefObject<HTMLElement | null>): {
  skipNext: () => void;
} {
  const recs = useRef<Map<string, Rec>>(new Map());
  const anims = useRef<Animation[]>([]);
  const clock = useRef<Animation | null>(null);
  const skipping = useRef(false);

  // A drop is already drawn in its new order (dnd-kit moved everything
  // there), so its commits only record layout. They can land over more than
  // one render, hence a frame rather than a single commit.
  const skipNext = useCallback(() => {
    skipping.current = true;
    const done = () => {
      skipping.current = false;
    };
    if (typeof requestAnimationFrame === "function")
      requestAnimationFrame(() => requestAnimationFrame(done));
    else setTimeout(done, 32);
  }, []);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = Array.from(
      root.querySelectorAll<HTMLElement>("[data-flip-key]")
    );
    const prev = recs.current;
    const moving = clock.current?.playState === "running";
    if (
      moving &&
      els.length === prev.size &&
      els.every((el) => {
        const r = prev.get(keyOf(el));
        return r?.el === el && r.mode === modeOf(el);
      })
    )
      return;

    const timing = moving ? clock.current?.effect?.getComputedTiming() : null;
    const p = typeof timing?.progress === "number" ? timing.progress : 1;
    anims.current.forEach((a) => a.cancel());
    anims.current = [];
    clock.current = null;
    clearFlags(root);

    const next = new Map<string, Rec>();
    for (const el of els) {
      const cs = getComputedStyle(el);
      const box = {
        left: el.offsetLeft,
        width: el.getBoundingClientRect().width
      };
      next.set(keyOf(el), {
        el,
        mode: modeOf(el),
        from: box,
        to: box,
        bg: cs.backgroundColor,
        opacity: cs.opacity,
        mark: markOf(el)?.getBoundingClientRect().width ?? 0
      });
    }
    recs.current = next;
    if (
      !prev.size ||
      skipping.current ||
      reducedMotion() ||
      typeof root.animate !== "function"
    )
      return;

    const started: Animation[] = [];
    const resize = (el: HTMLElement, width: number, kfs: Keyframe[]) => {
      el.dataset.resizing = "";
      el.dataset.flipWidth = String(width);
      const a = el.animate(kfs, { duration: DURATION, easing: EASING });
      a.onfinish = () => {
        delete el.dataset.resizing;
        delete el.dataset.flipWidth;
      };
      started.push(a);
    };

    const enter = (el: HTMLElement, r: Rec) => {
      r.from = { left: r.to.left, width: 0 };
      resize(el, r.to.width, [
        { width: "0px", opacity: 0 },
        { width: `${r.to.width}px`, opacity: r.opacity }
      ]);
    };

    const change = (el: HTMLElement, r: Rec, before: Rec) => {
      r.from = {
        left: lerp(before.from.left, before.to.left, p),
        width: lerp(before.from.width, before.to.width, p)
      };
      const swapped = before.el !== el;
      // A node kept across renders gets its colour change from its CSS
      // transition; a swapped one (tile ↔ group) starts from the old node's.
      const colours = (at: Rec): Keyframe =>
        swapped ? { backgroundColor: at.bg, opacity: at.opacity } : {};
      if (Math.abs(r.from.width - r.to.width) >= 0.5) {
        resize(el, r.to.width, [
          { width: `${r.from.width}px`, ...colours(before) },
          { width: `${r.to.width}px`, ...colours(r) }
        ]);
      } else if (swapped) {
        started.push(
          el.animate([colours(before), colours(r)], {
            duration: DURATION,
            easing: EASING
          })
        );
      }
      if (!swapped) return;
      const mark = markOf(el);
      const scale = before.mark && r.mark ? before.mark / r.mark : 1;
      if (mark && Math.abs(scale - 1) > 0.01)
        started.push(
          mark.animate(
            [{ transform: `scale(${scale})` }, { transform: "none" }],
            { duration: DURATION, easing: EASING }
          )
        );
      if (r.to.width > r.from.width)
        Array.from(el.children)
          .filter(
            (c): c is HTMLElement =>
              c instanceof HTMLElement &&
              !c.matches("[data-flip-mark]") &&
              !c.querySelector("[data-flip-mark]")
          )
          .forEach((c) =>
            started.push(c.animate([{ opacity: 0 }, { opacity: 1 }], FADE))
          );
    };

    for (const el of els) {
      const r = next.get(keyOf(el));
      const before = prev.get(keyOf(el));
      if (r && before) change(el, r, before);
      else if (r) enter(el, r);
    }

    // Widths are now drawn at their starting values; slide whatever reflow
    // alone doesn't bring back to where it was.
    for (const el of els) {
      const r = next.get(keyOf(el));
      const dx = r && prev.has(keyOf(el)) ? r.from.left - el.offsetLeft : 0;
      if (Math.abs(dx) >= 0.5) {
        const a = el.animate(
          [{ transform: `translateX(${dx}px)` }, { transform: "none" }],
          { duration: DURATION, easing: EASING }
        );
        started.push(a);
      }
    }

    anims.current = started;
    clock.current =
      started.find((a) => {
        const t = a.effect?.getTiming();
        return !t?.delay;
      }) ?? null;
  });

  return { skipNext };
}

/**
 * How much wider than their final layout the strip's animating elements are
 * drawn right now, so a measurement mid-animation can correct for it.
 */
export const flipDrift = (root: HTMLElement): number =>
  Array.from(root.querySelectorAll<HTMLElement>("[data-flip-width]")).reduce(
    (sum, el) =>
      sum +
      el.getBoundingClientRect().width -
      (Number(el.dataset.flipWidth) || 0),
    0
  );
